import { useEffect } from 'react'
import { Zap, DollarSign, Rocket, Users } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export default function Landing() {
  const navigate = useNavigate()

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add('visible')
      }),
      { threshold: 0.1 }
    )
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif", background: '#F7F2EB', minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Montserrat:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .reveal { opacity: 0; transform: translateY(32px); transition: opacity 0.7s ease, transform 0.7s ease; }
        .reveal.visible { opacity: 1; transform: translateY(0); }
        .reveal-delay-1 { transition-delay: 0.1s; }
        .reveal-delay-2 { transition-delay: 0.2s; }
        .reveal-delay-3 { transition-delay: 0.3s; }
        .reveal-delay-4 { transition-delay: 0.4s; }
        .nav-link { color: rgba(43,43,43,0.6); font-size: 13px; font-weight: 500; text-decoration: none; transition: color 0.2s; }
        .nav-link:hover { color: #B8734A; }
        .primary-btn { background: #B8734A; color: #fff; border: none; border-radius: 8px; padding: 13px 32px; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; letter-spacing: 0.05em; }
        .primary-btn:hover { background: #C9895F; transform: translateY(-1px); }
        .outline-btn { background: transparent; color: #B8734A; border: 1.5px solid #B8734A; border-radius: 8px; padding: 12px 28px; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; letter-spacing: 0.05em; }
        .outline-btn:hover { background: rgba(184,115,74,0.08); }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .float { animation: float 4s ease-in-out infinite; }
      `}</style>

      {/* Nav */}
      <nav style={{ background: '#fff', borderBottom: '1px solid #EDE6DA', padding: '0 2rem', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid #B8734A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Playfair Display, serif', fontSize: 13, fontWeight: 700, color: '#B8734A' }}>CS</div>
          <div>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 15, fontWeight: 600, color: '#2B2B2B', lineHeight: 1 }}>Collective Staff</div>
            <div style={{ fontSize: 9, color: '#8A9E8A', textTransform: 'uppercase', letterSpacing: '0.12em' }}>by Gallagher Collective Works</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {[['Features', '/features'], ['Pricing', '/pricing'], ['Platform', '/platform'], ['About', '/about']].map(([label, path]) => (
            <button key={path} onClick={() => navigate(path)} style={{ padding: '6px 14px', border: 'none', borderRadius: 6, background: 'transparent', color: 'rgba(43,43,43,0.6)', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}
              onMouseEnter={e => e.currentTarget.style.color = '#B8734A'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(43,43,43,0.6)'}>
              {label}
            </button>
          ))}
          <div style={{ width: 1, height: 20, background: '#EDE6DA', margin: '0 4px' }} />
          <button className="outline-btn" style={{ padding: '7px 16px', fontSize: 12 }} onClick={() => navigate('/login')}>Sign In</button>
          <button className="primary-btn" style={{ padding: '7px 16px', fontSize: 12 }} onClick={() => navigate('/demo')}>Try Demo →</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '7rem 2rem 5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
        <div>
          <div className="reveal" style={{ display: 'inline-block', background: 'rgba(184,115,74,0.1)', border: '1px solid rgba(184,115,74,0.3)', borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 600, color: '#B8734A', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
            HR Portal — Built for Real Teams
          </div>
          <h1 className="reveal reveal-delay-1" style={{ fontFamily: 'Playfair Display, serif', fontSize: '3.4rem', fontWeight: 700, color: '#2B2B2B', lineHeight: 1.15, marginBottom: '1.5rem' }}>
            Your team deserves<br /><em style={{ color: '#B8734A' }}>better HR tools.</em>
          </h1>
          <p className="reveal reveal-delay-2" style={{ fontSize: 16, color: '#6B7D6B', lineHeight: 1.8, marginBottom: '2rem', maxWidth: 440 }}>
            Collective Staff gives small businesses a complete HR workspace — onboarding, timekeeping, policies, helpdesk, calendars, and performance — beautifully simple and fully branded to feel like yours.
          </p>
          <div className="reveal reveal-delay-3" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: '2.5rem' }}>
            <button className="primary-btn" onClick={() => navigate('/demo')}>Try the Live Demo →</button>
            <button className="outline-btn" onClick={() => navigate('/features')}>See All Features</button>
          </div>
          <div className="reveal reveal-delay-4" style={{ display: 'flex', gap: 32 }}>
            {[['5 min', 'Setup time'], ['$0', 'Setup fee'], ['5–50', 'Employees']].map(([val, label]) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.6rem', color: '#B8734A', fontWeight: 700, lineHeight: 1 }}>{val}</div>
                <div style={{ fontSize: 11, color: '#8A9E8A', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating card */}
        <div className="reveal reveal-delay-2" style={{ position: 'relative' }}>
          <div className="float" style={{ background: '#fff', border: '1px solid #EDE6DA', borderRadius: 16, padding: '1.5rem', boxShadow: '0 16px 48px rgba(43,43,43,0.12)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 15, fontWeight: 600, color: '#2B2B2B' }}>Good morning, Jamie.</div>
              <div style={{ background: '#B8734A', color: '#fff', fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20 }}>+ Clock In</div>
            </div>
            {[['Hours This Week', '24.5', 'On Track', '#4A7C59'], ['PTO Balance', '11 days', '2 pending', '#B8734A'], ['Tasks Due', '3', '1 urgent', '#4A6B8A']].map(([label, val, pill, color]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#F7F2EB', borderRadius: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: '#6B7D6B' }}>{label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 15, color: '#2B2B2B', fontWeight: 600 }}>{val}</span>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 600, background: color + '22', color }}>{pill}</span>
                </div>
              </div>
            ))}
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #EDE6DA' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#B8734A', marginBottom: 4 }}>Spring Schedule Change — All Staff</div>
              <div style={{ fontSize: 11, color: '#6B7D6B' }}>Effective April 14, office hours shift to 8am–4pm...</div>
            </div>
          </div>
          <div style={{ position: 'absolute', top: -16, right: -16, background: '#2B2B2B', color: '#fff', borderRadius: 12, padding: '10px 16px', fontSize: 12, fontWeight: 600, boxShadow: '0 4px 16px rgba(43,43,43,0.2)' }}>
            ✓ Branded to your company
          </div>
        </div>
      </section>

      {/* Social proof strip */}
      <div style={{ background: '#2B2B2B', padding: '1.25rem 2rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap' }}>
          {['Built for 5–50 employees', 'No IT department required', 'Mobile responsive', 'White-labeled to your brand', 'No long-term contracts'].map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'rgba(217,195,163,0.7)', fontWeight: 500 }}>
              <span style={{ color: '#B8734A', fontSize: 14 }}>✦</span> {item}
            </div>
          ))}
        </div>
      </div>

      {/* Quick nav cards */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '4rem 2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h2 className="reveal" style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', fontWeight: 700, color: '#2B2B2B', marginBottom: '0.5rem' }}>Everything your team needs.</h2>
          <p className="reveal reveal-delay-1" style={{ fontSize: 14, color: '#6B7D6B' }}>Explore each section to learn more.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {[
            { Icon: Zap, iconColor: '#B8734A', title: 'Features', desc: '16 modules: onboarding, clock in/out, helpdesk & more — onboarding, policies, timesheets & more', path: '/features', color: '#B8734A' },
            { Icon: DollarSign, iconColor: '#4A7C59', title: 'Pricing', desc: 'Plans from $59/mo — no contracts, no surprises', path: '/pricing', color: '#4A7C59' },
            { Icon: Rocket, iconColor: '#4A6B8A', title: 'Platform', desc: 'The full GCW product suite — Staff, Hospitality, Fleet & more', path: '/platform', color: '#4A6B8A' },
            { Icon: Users, iconColor: '#6B7D6B', title: 'About', desc: 'Meet John & Tiffany Gallagher — and get in touch', path: '/about', color: '#6B7D6B' },
          ].map((card, i) => (
            <div key={card.title}
              className={`reveal reveal-delay-${i + 1}`}
              onClick={() => navigate(card.path)}
              style={{ background: '#fff', border: '1px solid #EDE6DA', borderRadius: 12, padding: '1.5rem', cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = card.color; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${card.color}22`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#EDE6DA'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ width:44,height:44,borderRadius:10,background:card.iconColor+'18',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:'0.75rem' }}><card.Icon size={22} color={card.iconColor} strokeWidth={1.75}/></div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 16, fontWeight: 600, color: '#2B2B2B', marginBottom: '0.4rem' }}>{card.title}</div>
              <div style={{ fontSize: 12, color: '#6B7D6B', lineHeight: 1.6, marginBottom: '0.75rem' }}>{card.desc}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: card.color }}>Learn more →</div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: '#1E1E1E', padding: '2rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 15, color: '#fff', fontWeight: 600, marginBottom: 3 }}>Collective Staff</div>
            <div style={{ fontSize: 10, color: 'rgba(217,195,163,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>A Gallagher Collective Works Product</div>
          </div>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            {[['Features', '/features'], ['Pricing', '/pricing'], ['Platform', '/platform'], ['About', '/about'], ['Demo', '/demo']].map(([label, path]) => (
              <button key={path} onClick={() => navigate(path)} style={{ background: 'none', border: 'none', fontSize: 12, color: 'rgba(217,195,163,0.4)', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}
                onMouseEnter={e => e.currentTarget.style.color = '#B8734A'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(217,195,163,0.4)'}>
                {label}
              </button>
            ))}
            <a href="mailto:info@gallaghercollectiveworks.com" style={{ fontSize: 12, color: '#B8734A', textDecoration: 'none', fontWeight: 500 }}>info@gallaghercollectiveworks.com</a>
          </div>
          <div style={{ fontSize: 11, color: 'rgba(217,195,163,0.25)' }}>© 2025 Gallagher Collective Works LLC</div>
        </div>
      </footer>
    </div>
  )
}